import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, Loader2, ChevronLeft, ChevronRight, AlertTriangle, RotateCw } from 'lucide-react';
import PenjagaMenuPkg from '@/components/pkg/PenjagaMenuPkg';
import RiwayatAksiPenilaian from '@/components/pkg/RiwayatAksiPenilaian';
import RiwayatPerubahanInstrumen from '@/components/pkg/RiwayatPerubahanInstrumen';
import RiwayatUnduhan from '@/components/pkg/RiwayatUnduhan';
import SaringanAudit, { SARINGAN_AUDIT_KOSONG } from '@/components/pkg/SaringanAudit';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatWIBPanjang } from '@/lib/datetime';
import { KATEGORI_AUDIT, daftarAudit, riwayatAksiGuru } from '@/lib/pkgAudit';
import { ambilRekap, daftarPeriodeRekap } from '@/lib/pkgRekap';

const PER_HALAMAN = 10;

// Riwayat aksi lembar penilaian per guru (kepala): pilih guru dari rekap periode berjalan.
function PanelPerGuru() {
  const [guru, setGuru] = useState([]);
  const [pilih, setPilih] = useState('');

  useEffect(() => {
    ambilRekap().then((r) => setGuru(r.guru || [])).catch(() => setGuru([]));
  }, []);
  const muat = useCallback(() => riwayatAksiGuru(pilih), [pilih]);

  return (
    <Card data-testid="pkg-audit-per-guru">
      <CardHeader className="pb-3 space-y-2">
        <CardTitle className="text-base">Riwayat Aksi Penilaian per Guru</CardTitle>
        <Select value={pilih} onValueChange={setPilih}>
          <SelectTrigger className="h-9 sm:w-80" data-testid="pkg-audit-pilih-guru"><SelectValue placeholder="Pilih guru yang dinilai" /></SelectTrigger>
          <SelectContent>
            {guru.map((g) => <SelectItem key={g.guru_id} value={g.guru_id}>{g.nama}{g.penilai_nama ? ` — ${g.penilai_nama}` : ''}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {pilih ? <RiwayatAksiPenilaian muat={muat} kunci={pilih} /> : <p className="py-4 text-center text-sm text-slate-500">Pilih guru untuk melihat riwayat aksi lembarnya.</p>}
      </CardContent>
    </Card>
  );
}

const SEMUA_PERIODE = '__semua__';

// Riwayat unduhan berkas (kepala): saringan semua periode atau periode tertentu.
function PanelUnduhan() {
  const [periode, setPeriode] = useState([]);
  const [pilih, setPilih] = useState(SEMUA_PERIODE);

  useEffect(() => { daftarPeriodeRekap().then(setPeriode).catch(() => setPeriode([])); }, []);

  return (
    <div className="space-y-3" data-testid="pkg-audit-unduhan">
      <Select value={pilih} onValueChange={setPilih}>
        <SelectTrigger className="h-9 bg-white sm:w-80" data-testid="pkg-audit-unduhan-periode"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={SEMUA_PERIODE}>Semua periode</SelectItem>
          {periode.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama_periode}</SelectItem>)}
        </SelectContent>
      </Select>
      <RiwayatUnduhan periodeId={pilih === SEMUA_PERIODE ? undefined : pilih} />
    </div>
  );
}

function IsiHalamanAudit() {
  const [halaman, setHalaman] = useState(1);
  const [saringan, setSaringan] = useState(SARINGAN_AUDIT_KOSONG);
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState(null);

  const muat = useCallback(async () => {
    setGalat(null);
    try {
      const { kategori, aksi, dari, sampai, cari } = saringan;
      setData(await daftarAudit({ halaman, perHalaman: PER_HALAMAN, kategori, aksi, dari, sampai, cari }));
    } catch (e) {
      setGalat(e);
    }
  }, [halaman, saringan]);

  // Saringan berubah -> kembali ke halaman pertama.
  const ubahSaringan = useCallback((baru) => { setSaringan(baru); setHalaman(1); }, []);

  useEffect(() => { muat(); }, [muat]);

  if (galat) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center" role="alert">
        <AlertTriangle className="h-8 w-8 text-amber-600" />
        <p className="text-sm text-slate-700">{galat.message || 'Gagal memuat jejak audit.'}</p>
        <Button variant="outline" size="sm" onClick={muat} className="gap-2"><RotateCw className="h-4 w-4" /> Coba lagi</Button>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#006837]" /></div>;

  const jumlahHalaman = Math.max(1, Math.ceil(data.total / data.per_halaman));

  return (
    <Card data-testid="pkg-audit">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-[#006837]" /> Jejak Audit PKG</CardTitle>
        <p className="text-xs text-slate-500">Riwayat aksi penilaian, perubahan instrumen, susunan periode & SK, cetak/unduh, dan akses. Aksi yang berulang dicatat harian beserta jumlahnya.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <SaringanAudit nilai={saringan} onUbah={ubahSaringan} />
        {data.item.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">
            {Object.values(saringan).some(Boolean) ? 'Tidak ada aktivitas yang cocok dengan saringan.' : 'Belum ada aktivitas tercatat.'}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Waktu</TableHead>
                  <TableHead>Aksi</TableHead>
                  <TableHead>Pelaku</TableHead>
                  <TableHead>Ringkasan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.item.map((a) => {
                  const kat = KATEGORI_AUDIT[a.kategori] || KATEGORI_AUDIT.akses;
                  return (
                    <TableRow key={a.id} data-testid={`pkg-audit-${a.id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">
                        {formatWIBPanjang(a.terakhir || a.timestamp)}
                        {a.jumlah > 1 && <div className="text-xs text-slate-400">{a.jumlah}× hari itu</div>}
                      </TableCell>
                      <TableCell className="min-w-[180px]">
                        <div className="text-sm font-medium text-slate-900">{a.label}</div>
                        <Badge className={`mt-0.5 ${kat.kelas}`}>{kat.label}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-slate-700">{a.aktor_nama || '-'}</TableCell>
                      <TableCell className="min-w-[220px] text-sm text-slate-600">{a.ringkasan || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="tabular-nums">{data.total} entri · halaman {data.halaman}/{jumlahHalaman}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-8" disabled={halaman <= 1} onClick={() => setHalaman((h) => h - 1)} aria-label="Halaman sebelumnya" data-testid="pkg-audit-sebelumnya">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" className="h-8" disabled={halaman >= jumlahHalaman} onClick={() => setHalaman((h) => h + 1)} aria-label="Halaman berikutnya" data-testid="pkg-audit-berikutnya">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Jejak audit PKG (khusus kepala madrasah): daftar aksi terbaru dengan kategori, pelaku, dan ringkasan.
// Data dari GET /pkg/audit & /pkg/audit/guru/{guru_id} (lib/pkgAudit).
const TAB_AUDIT = ['semua', 'guru', 'instrumen', 'unduhan'];

export default function PkgAuditPage() {
  // Tab aktif disimpan di URL (?tab=) agar dapat ditautkan langsung, mis. /pkg/audit?tab=unduhan.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TAB_AUDIT.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'semua';
  const gantiTab = (t) => setSearchParams((sp) => { const baru = new URLSearchParams(sp); baru.set('tab', t); return baru; });

  return (
    <PenjagaMenuPkg menuKey="audit">
      <Tabs value={tab} onValueChange={gantiTab}>
        <TabsList>
          <TabsTrigger value="semua" data-testid="pkg-audit-tab-semua">Semua Aksi</TabsTrigger>
          <TabsTrigger value="guru" data-testid="pkg-audit-tab-guru">Per Guru</TabsTrigger>
          <TabsTrigger value="instrumen" data-testid="pkg-audit-tab-instrumen">Instrumen</TabsTrigger>
          <TabsTrigger value="unduhan" data-testid="pkg-audit-tab-unduhan">Unduhan</TabsTrigger>
        </TabsList>
        <TabsContent value="semua"><IsiHalamanAudit /></TabsContent>
        <TabsContent value="guru"><PanelPerGuru /></TabsContent>
        <TabsContent value="instrumen">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Riwayat Perubahan Instrumen per Versi</CardTitle></CardHeader>
            <CardContent><RiwayatPerubahanInstrumen /></CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="unduhan"><PanelUnduhan /></TabsContent>
      </Tabs>
    </PenjagaMenuPkg>
  );
}
